// Search across every change line and customer note on the changelog.

import { cx } from '../../../../components/admin/console';
import { AppIcon } from './AppIcon';
import { AuthorMark, CommitChip, KIND_STYLE } from './parts';
import { PENDING_ID, highlight, nzShortDay, type SearchGroup, type SectionKind } from '../data/changelog';

const KIND_WORD: Record<SectionKind | 'note', string> = {
  features: 'Feature',
  fixes: 'Fix',
  speedups: 'Speed-up',
  reverts: 'Revert',
  breaking: 'Breaking',
  other: 'Change',
  note: 'Shown',
};

export function SearchResults({ q, groups, onOpen }: { q: string; groups: SearchGroup[]; onOpen: (g: SearchGroup) => void }) {
  const hits = groups.reduce((n, g) => n + g.hits.length, 0);
  const updates = new Set(groups.map((g) => g.updateId)).size;
  return (
    <section aria-label="Search results" className="space-y-3">
      <p className="text-[15px] text-slate-300" aria-live="polite">
        {hits ? (
          <>
            <strong className="font-semibold text-white">{hits} {hits === 1 ? 'change' : 'changes'}</strong> {hits === 1 ? 'matches' : 'match'} “{q.trim()}”, in {updates} {updates === 1 ? 'update' : 'updates'}
          </>
        ) : (
          <>Nothing matches “{q.trim()}”. Try a shorter or different word.</>
        )}
      </p>
      {groups.map((g) => (
        <article key={`${g.updateId}-${g.app}-${g.version}`} className="overflow-hidden rounded-2xl border border-slate-700 bg-slate-800/50">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-slate-700/60 px-4 py-2.5">
            <AppIcon app={g.app} size={28} />
            <span className="text-[15px] font-semibold text-white">
              {g.name} <span className="font-mono text-sm text-slate-200">{g.version}</span>
            </span>
            <span className="text-sm text-slate-400">
              {g.updateId === PENDING_ID ? 'Not sent to Scouts yet' : `${nzShortDay(g.firstOfferedAt)} · ${g.sentToScouts ? 'sent to Scouts' : 'inside a later update'}`}
            </span>
            <button
              type="button"
              onClick={() => onOpen(g)}
              className="ml-auto inline-flex min-h-[40px] items-center rounded text-sm font-medium text-cyan-400 hover:text-cyan-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
            >
              {g.updateId === PENDING_ID ? 'Open in Not sent yet' : 'Open this update'}
            </button>
          </div>
          <ul className="px-4 pb-2.5 pt-1.5">
            {g.hits.map((h, i) => (
              <li key={i} className="flex items-baseline gap-3 py-1.5 text-[15px] leading-snug text-slate-300">
                <span className={cx('w-[68px] flex-none text-[13px] font-semibold', h.kind === 'note' ? 'text-slate-400' : KIND_STYLE[h.kind].head)}>{KIND_WORD[h.kind]}</span>
                <span className="min-w-0 flex-1">
                  {highlight(h.text, q).map((p, j) =>
                    p.hit ? <mark key={j} className="rounded-[3px] bg-yellow-400/25 px-0.5 text-amber-100">{p.text}</mark> : <span key={j}>{p.text}</span>,
                  )}
                </span>
                {h.sha && h.url && (
                  <span className="flex flex-none items-center gap-2">
                    {h.author && <AuthorMark author={h.author} />}
                    <CommitChip sha={h.sha} url={h.url} hit={h.bySha} />
                  </span>
                )}
              </li>
            ))}
          </ul>
        </article>
      ))}
    </section>
  );
}
